import React from 'react';
import { useTranslation } from 'react-i18next';
import { Edit, Trash2, Power, PowerOff, Calendar, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const AbsenceReasonsTable = ({
  filteredReasons,
  loading,
  categories,
  handleToggleStatus,
  handleEdit,
  setSelectedReason,
  setShowDeleteDialog,
}) => {
  const { t, i18n } = useTranslation();

  const getCategoryLabel = (category) => {
    return t(`absenceReasons.categories.${category}`, category);
  };

  const getCategoryIcon = (category) => {
    const cat = categories.find((c) => c.value === category);
    return cat ? cat.icon : '📌';
  };

  const getLocalizedName = (reason) => {
    if (i18n.language === 'ar' && reason.name_ar) return reason.name_ar;
    if (i18n.language === 'en' && reason.name_en) return reason.name_en;
    return reason.name;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('absenceReasons.listTitle')}</CardTitle>
        <CardDescription>{filteredReasons.length} {t('absenceReasons.listTitle').toLowerCase()}</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12" />
                  <TableHead>{t('common.code', 'Code')}</TableHead>
                  <TableHead>{t('common.name', 'Name')}</TableHead>
                  <TableHead>{t('common.category', 'Category')}</TableHead>
                  <TableHead>{t('common.paid', 'Paid')}</TableHead>
                  <TableHead>{t('common.balance', 'Balance')}</TableHead>
                  <TableHead>{t('common.status', 'Status')}</TableHead>
                  <TableHead className="text-right">{t('common.actions', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReasons.map((reason) => (
                  <TableRow key={reason.id}>
                    <TableCell>
                      <div
                        className="w-8 h-8 rounded-full border-2 border-border"
                        style={{ backgroundColor: reason.color }}
                        title={reason.color}
                      />
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">{reason.code}</Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{getLocalizedName(reason)}</p>
                        {reason.description && (
                          <p className="text-xs text-muted-foreground truncate max-w-xs">
                            {reason.description}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span>{getCategoryIcon(reason.category)}</span>
                        <span className="text-sm">{getCategoryLabel(reason.category)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {reason.is_paid ? (
                        <Badge variant="default" className="bg-green-500">{reason.payment_rate}%</Badge>
                      ) : (
                        <Badge variant="secondary">{t('common.unpaid', 'Unpaid')}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {reason.has_balance ? (
                        <Badge variant="outline" className="gap-1">
                          <Calendar className="h-3 w-3" />
                          {reason.default_annual_balance} {reason.unit === 'DAY' ? t('common.days', 'd') : t('common.hours', 'h')}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {reason.is_active ? (
                        <Badge variant="default">{t('common.active', 'Active')}</Badge>
                      ) : (
                        <Badge variant="secondary">{t('common.inactive', 'Inactive')}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleToggleStatus(reason)}
                          title={reason.is_active ? t('common.deactivate', 'Deactivate') : t('common.activate', 'Activate')}
                        >
                          {reason.is_active ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(reason)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => { setSelectedReason(reason); setShowDeleteDialog(true); }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {filteredReasons.length === 0 && !loading && (
          <div className="text-center py-8">
            <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <h3 className="mt-4 text-lg font-semibold">{t('common.noResults', 'No results found')}</h3>
            <p className="text-muted-foreground">
              {t('absenceReasons.emptyDesc', 'Start by adding an absence reason.')}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AbsenceReasonsTable;
